/**
 * usePreferenceColumns — column-menu controller backed by `usePreference`.
 *
 * Adapter that produces the same `UseDatasheetColumnsMenuResult<TCol>` surface
 * as the legacy `useDatasheetColumns`, but persists state via the generic
 * preference registry instead of module-specific RTK Query endpoints.
 *
 * Storage shape (per sub-key, e.g. mode='pucontract'):
 *   {
 *     columnOrder:   string[],   // full order including hidden
 *     columnVisible: string[],   // subset of order — order preserved
 *     sortBy:        {key, dir} | null  // not used by this hook
 *   }
 *
 * `reset()` deletes the user's sub-key delta → UI falls back to the baseline
 * `default` row (seeded by SQL migration).
 *
 * NOTE on schema drift: if the static `allColumns` array gains a new column
 * after the user has persisted state, that new column appears AFTER all
 * existing entries and is visible by default. This matches the "default delta"
 * model — adding a column never silently hides it from existing users.
 */
import { useCallback, useMemo, useState, type DragEvent } from 'react'

import { usePreference } from '@/hooks/usePreference'
import { useDeleteUserTableWidthsMutation } from '@/store/api/userTableWidthsApi'
import type { DatasheetColumnLike, UseDatasheetColumnsMenuResult } from './types'

interface PreferenceColumnState {
  columnOrder: string[]
  columnVisible: string[]
  sortBy: { key: string; dir: 'asc' | 'desc' } | null
}

export interface UsePreferenceColumnsParams<TCol extends DatasheetColumnLike> {
  /** Registry scope, e.g. 'purchase'. */
  scope: string
  /** Registry key, e.g. 'columns'. */
  prefKey: string
  /** Sub-key for nested preferences, e.g. mode='pucontract'. */
  subKey: string
  /** Static column definitions in baseline order. */
  allColumns: TCol[]
  /** Optional table_key used by `useColumnWidths`. When set, `resetToDefault`
   *  also clears the user's per-column widths for that table so the UI returns
   *  to the baseline widths defined in the column metadata, not the user's
   *  last drag-resize. */
  widthsTableKey?: string
}

export function usePreferenceColumns<TCol extends DatasheetColumnLike>(
  params: UsePreferenceColumnsParams<TCol>,
): UseDatasheetColumnsMenuResult<TCol> {
  const { scope, prefKey, subKey, allColumns, widthsTableKey } = params
  const pref = usePreference<PreferenceColumnState>(scope, prefKey, subKey)
  const [deleteUserTableWidths] = useDeleteUserTableWidthsMutation()

  const allKeys = useMemo(() => allColumns.map((c) => String(c.key)), [allColumns])
  const columnsByKey = useMemo(
    () => new Map(allColumns.map((c) => [String(c.key), c] as const)),
    [allColumns],
  )

  // Normalise effective state: prune unknown keys; append any new static keys
  // at the end with visible=true so a deployed schema bump never hides cols.
  //
  // Schema-drift visible logic:
  //   - persistedOrder = các key đã tồn tại khi user save lần cuối.
  //   - Keys CHƯA có trong persistedOrder (= keys mới sau khi user save) → AUTO
  //     visible. Tránh case pivot page (Quality Standard, Deduction Rate) thêm
  //     dynamic quality_factory column mới → bị auto-hide do columnVisible đã
  //     save không có key đó.
  //   - Keys EXPLICITLY hidden bởi user (có trong persistedOrder nhưng KHÔNG
  //     trong columnVisible) → vẫn ẩn (respect user choice).
  const { menuOrder, hiddenSet } = useMemo(() => {
    const seen = new Set<string>()
    const order: string[] = []
    for (const k of pref.value.columnOrder ?? []) {
      if (!columnsByKey.has(k) || seen.has(k)) continue
      order.push(k)
      seen.add(k)
    }
    for (const k of allKeys) {
      if (!seen.has(k)) {
        order.push(k)
        seen.add(k)
      }
    }
    const persistedOrderSet = new Set(pref.value.columnOrder ?? [])
    const visible = pref.value.columnVisible
      ? new Set<string>([
          ...pref.value.columnVisible,
          // NEW keys (không nằm trong persisted order) → auto-visible.
          ...allKeys.filter((k) => !persistedOrderSet.has(k)),
        ])
      : new Set(order)
    const hidden = new Set<string>(order.filter((k) => !visible.has(k)))
    return { menuOrder: order, hiddenSet: hidden }
  }, [allKeys, columnsByKey, pref.value.columnOrder, pref.value.columnVisible])

  const persist = useCallback(
    (nextOrder: string[], nextHidden: Set<string>) => {
      const nextVisible = nextOrder.filter((k) => !nextHidden.has(k))
      void pref
        .patch({
          columnOrder: nextOrder,
          columnVisible: nextVisible,
        })
        .catch(() => undefined)
    },
    [pref],
  )

  const toggleColumn = useCallback(
    (key: string) => {
      const visibleCount = menuOrder.length - hiddenSet.size
      const nextHidden = new Set(hiddenSet)
      if (nextHidden.has(key)) {
        nextHidden.delete(key)
      } else {
        if (visibleCount <= 1) return
        nextHidden.add(key)
      }
      persist(menuOrder, nextHidden)
    },
    [hiddenSet, menuOrder, persist],
  )

  const resetToDefault = useCallback(() => {
    void pref.reset().catch(() => undefined)
    if (widthsTableKey) {
      void deleteUserTableWidths(widthsTableKey)
        .unwrap()
        .catch(() => undefined)
    }
  }, [deleteUserTableWidths, pref, widthsTableKey])

  /* ───────── Drag & drop reorder ───────── */
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const onDragStart = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      setDragKey(key)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', key)
    },
    [],
  )

  const onDragOver = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      if (!dragKey || dragKey === key) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverKey(key)
    },
    [dragKey],
  )

  const onDrop = useCallback(
    (targetKey: string) => (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      const sourceKey = dragKey
      setDragKey(null)
      setDragOverKey(null)
      if (!sourceKey || sourceKey === targetKey) return
      const from = menuOrder.indexOf(sourceKey)
      const to = menuOrder.indexOf(targetKey)
      if (from < 0 || to < 0) return
      const next = [...menuOrder]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      persist(next, hiddenSet)
    },
    [dragKey, hiddenSet, menuOrder, persist],
  )

  const onDragEnd = useCallback(() => {
    setDragKey(null)
    setDragOverKey(null)
  }, [])

  /* ───────── Memoised derived lists ───────── */
  const visibleColumns = useMemo<TCol[]>(
    () =>
      menuOrder
        .filter((k) => !hiddenSet.has(k))
        .flatMap((k) => {
          const col = columnsByKey.get(k)
          return col ? [col] : []
        }),
    [columnsByKey, hiddenSet, menuOrder],
  )

  const menuItems = useMemo<TCol[]>(
    () =>
      menuOrder.flatMap((k) => {
        const col = columnsByKey.get(k)
        return col ? [col] : []
      }),
    [columnsByKey, menuOrder],
  )

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const openMenu = useCallback((anchor: HTMLElement) => {
    setMenuAnchor(anchor)
    // Blur trigger sau khi giữ anchor: tránh warning "aria-hidden on focused
    // element" (MUI v5) — Popover áp aria-hidden lên #root trước khi focus-trap
    // dời focus khỏi nút mở. anchorEl là DOM node nên vẫn anchor đúng sau blur.
    anchor.blur()
  }, [])
  const closeMenu = useCallback(() => setMenuAnchor(null), [])

  return {
    visibleColumns,
    menuItems,
    hiddenSet,
    menuAnchor,
    openMenu,
    closeMenu,
    toggleColumn,
    resetToDefault,
    dragKey,
    dragOverKey,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }
}
